/**
 * consultaApi — chamadas à API de Consulta (M7) sobre o ApiClient base.
 *
 * Listas seguem {data, nextCursor} no topo; ficha/pastas/download vêm no nível
 * superior (contrato congelado do M7). Nenhum dado pessoal é logado (RN-M11-001).
 */

import { ApiClient } from './apiClient';
import {
  DownloadOriginal,
  EscolaFicha,
  EscolaFiltros,
  EscolaListItem,
  FiltroVisitasQuery,
  PaginaLista,
  Pastas,
  VisitaFicha,
  VisitaFiltros,
  VisitaListItem,
} from './consulta-types';

interface EnvelopeLista<T> {
  readonly data: readonly T[];
  readonly nextCursor: string | null;
}

function montarQuery(filtros: Record<string, unknown>): string {
  const query = new URLSearchParams();
  for (const [chave, valor] of Object.entries(filtros)) {
    if (valor === undefined || valor === null || valor === '' || valor === false) {
      continue;
    }
    query.set(chave, String(valor));
  }
  const s = query.toString();
  return s.length > 0 ? `?${s}` : '';
}

/**
 * O filtro aplicado da lista na forma de query (ADR-036): sem paginação, sem vazios, `observacoes`/`mudou` como "1" —
 * é o critério que vai à estimativa e ao `POST /reprocessamentos` (o conjunto é exatamente o da tela).
 */
export function filtroAplicado(filtros: VisitaFiltros): FiltroVisitasQuery {
  const saida: Record<string, string> = {};
  for (const [chave, valor] of Object.entries(filtros)) {
    if (chave === 'cursor' || chave === 'limit') continue;
    if (valor === undefined || valor === null || valor === '' || valor === false) continue;
    saida[chave] = valor === true ? '1' : String(valor);
  }
  return saida;
}

export class ConsultaApi {
  constructor(private readonly client: ApiClient) {}

  /** GET /escolas — lista paginada (ou grupo "não identificada"). */
  async listarEscolas(filtros: EscolaFiltros = {}): Promise<PaginaLista<EscolaListItem>> {
    // ADR-041: `comVisitas` no formato do contrato (`1`); fora do grupo "não identificada" (lá o backend recusa).
    const { comVisitas, ...resto } = filtros;
    const query = { ...(comVisitas && !resto.grupo ? { comVisitas: '1' } : {}), ...resto };
    const resp = await this.client.request<EnvelopeLista<EscolaListItem>>(
      `/escolas${montarQuery(query as Record<string, unknown>)}`,
    );
    return { itens: resp.data, nextCursor: resp.nextCursor };
  }

  /** GET /escolas/{inep} — ficha + linha do tempo. */
  async obterEscola(inep: string): Promise<EscolaFicha> {
    return this.client.request<EscolaFicha>(`/escolas/${encodeURIComponent(inep)}`);
  }

  /** GET /visitas — lista paginada com filtros combinados. */
  async listarVisitas(filtros: VisitaFiltros = {}): Promise<PaginaLista<VisitaListItem>> {
    // ADR-035/036/038 e ajustes-telas-amostra: o backend aceita só `observacoes=1`, `mudou=1`, `incluirReferencias=1`
    // e `outrasEvidencias=1` (outro valor → 400).
    const { observacoes, mudou, incluirReferencias, outrasEvidencias, ...resto } = filtros;
    const query = {
      ...(observacoes ? { observacoes: '1' } : {}),
      ...(outrasEvidencias ? { outrasEvidencias: '1' } : {}),
      ...(mudou ? { mudou: '1' } : {}),
      ...(incluirReferencias ? { incluirReferencias: '1' } : {}),
      ...resto,
    };
    const resp = await this.client.request<EnvelopeLista<VisitaListItem> & { total?: number }>(
      `/visitas${montarQuery(query as Record<string, unknown>)}`,
    );
    return {
      itens: resp.data,
      nextCursor: resp.nextCursor,
      ...(typeof resp.total === 'number' ? { total: resp.total } : {}),
    };
  }

  /** GET /visitas/{id} — ficha completa (split-view). */
  async obterVisita(id: string): Promise<VisitaFicha> {
    return this.client.request<VisitaFicha>(`/visitas/${encodeURIComponent(id)}`);
  }

  /** GET /visitas/{id}/original — URL de download do original (auditada). */
  async baixarOriginal(id: string): Promise<DownloadOriginal> {
    return this.client.request<DownloadOriginal>(
      `/visitas/${encodeURIComponent(id)}/original`,
    );
  }

  /** GET /pastas?caminho=... — árvore de pastas (nó atual via query `caminho`). */
  async navegarPastas(caminho?: string): Promise<Pastas> {
    const query = caminho && caminho.length > 0 ? `?caminho=${encodeURIComponent(caminho)}` : '';
    return this.client.request<Pastas>(`/pastas${query}`);
  }
}
